// src/App.js
import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'

import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Tickets from './pages/Tickets'
import TicketsArea from './pages/TicketsArea'
import Splash from './pages/Splash'
import CrearPersonal from './pages/CrearPersonal'
import BuscarRut from './pages/BuscarRut'
import ListarPersonal from './pages/ListarPersonal'
import CargarProgramacion from './pages/CargarProgramacion'
import AlmuerzoDelDia from './pages/AlmuerzoDelDia'
import PronosticoMes from './pages/PronosticoMes'
import DashboardAlmuerzos from './pages/DashboardAlmuerzos'
import MantenedorMenuMes from './pages/MantenedorMenu'
import ReportesGanancia from './pages/ReporteGanancia'
import AdminLayout from './layouts/AdminLayout'
import DashboardAlmuerzosAdmin from './pages/DashboardAlmuerzosAdmin'
import AlmuerzosMes from './pages/AlmuerzosMes'
import MantenedorUsuarios from './pages/MantenedorUsuarios'
import Reserva from './pages/Reserva'
import DashboardAlmuerzosSede from './pages/DashboardAlmuerzosSede';

// ==========================
// Helpers Auth
// ==========================
function getAuth() {
  // ✅ Nuevo formato recomendado: localStorage.auth (JSON)
  const raw = localStorage.getItem('auth')
  if (raw) {
    try {
      const parsed = JSON.parse(raw)
      return {
        usuario: parsed.usuario || parsed.user || '',
        rol: (parsed.rol || parsed.role || '').toUpperCase(),
        maquinas: Array.isArray(parsed.maquinas) ? parsed.maquinas : [],
        token: parsed.token || '',
      }
    } catch (e) {
      // si auth está corrupto, caemos al modo antiguo
    }
  }

  // ✅ Compatibilidad con tu lógica antigua
  const usuarioLegacy = localStorage.getItem('usuario') || ''
  if (usuarioLegacy) {
    return {
      usuario: usuarioLegacy,
      rol: usuarioLegacy === 'Admin' ? 'ADMIN' : 'USER',
      maquinas: [],
      token: '',
    }
  }

  return { usuario: '', rol: '', maquinas: [], token: '' }
}

function isLoggedIn() {
  const a = getAuth()
  return Boolean(a.usuario)
}

// ==========================
// Route Guards
// ==========================
const PrivateRoute = ({ children }) => {
  return isLoggedIn() ? children : <Navigate to="/" replace />
}

/**
 * RoleRoute:
 * - allowedRoles: ['ADMIN', 'COCINA', 'AUDITOR', ...]
 */
const RoleRoute = ({ allowedRoles = [], children }) => {
  const a = getAuth()
  if (!isLoggedIn()) return <Navigate to="/" replace />
  if (allowedRoles.length === 0) return children
  if (allowedRoles.includes(a.rol)) return children
  return <Navigate to="/dashboard" replace />
}

/**
 * MachineRoute:
 * - Protege páginas que dependen de máquina/cocina.
 * - Si es ADMIN pasa siempre.
 * - Si no es admin, exige que tenga al menos 1 máquina asignada.
 */
const MachineRoute = ({ requiredMachineIds = null, children }) => {
  const a = getAuth()
  if (!isLoggedIn()) return <Navigate to="/" replace />

  // ADMIN ve todo
  if (a.rol === 'ADMIN') return children

  // debe tener máquinas asignadas
  if (!Array.isArray(a.maquinas) || a.maquinas.length === 0) {
    return <Navigate to="/dashboard" replace />
  }

  // Si la ruta exige máquinas específicas
  if (Array.isArray(requiredMachineIds) && requiredMachineIds.length > 0) {
    const ok = requiredMachineIds.some((id) => a.maquinas.includes(id))
    if (!ok) return <Navigate to="/dashboard" replace />
  }

  return children
}

// ==========================
// App
// ==========================
const App = () => {
  return (
    <Router>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Login />} />
        <Route path="/reserva" element={<Reserva />} />
        <Route path="/almuerzoMes" element={<AlmuerzosMes />} />

        {/* Admin layout (privado) */}
        <Route
          element={
            <PrivateRoute>
              <AdminLayout />
            </PrivateRoute>
          }
        >
          {/* Dashboard base (cualquier logueado) */}
          <Route path="/dashboard" element={<DashboardAlmuerzos />} />

          {/* Tickets: ADMIN y COCINA (pero cocina filtrada por sus máquinas) */}
          <Route
            path="/tickets"
            element={
              <RoleRoute allowedRoles={['ADMIN', 'COCINA']}>
                <MachineRoute>
                  <Tickets />
                </MachineRoute>
              </RoleRoute>
            }
          />          {/* Tickets: ADMIN y COCINA (pero cocina filtrada por sus máquinas) */}
       
          <Route
            path="/tickets-por-area"
            element={
              <RoleRoute allowedRoles={['ADMIN', 'COCINA', 'AUDITOR']}>
                <MachineRoute>
                  <TicketsArea />
                </MachineRoute>
              </RoleRoute>
            }
          />

          {/* Gestión personal: solo ADMIN */}
          <Route
            path="/buscar-rut"
            element={
              <RoleRoute allowedRoles={['ADMIN']}>
                <BuscarRut />
              </RoleRoute>
            }
          />
          <Route path="/splash" element={<Splash />} />

          <Route
            path="/crear-personal"
            element={
              <RoleRoute allowedRoles={['ADMIN' , 'COCINA']}>
                <CrearPersonal />
              </RoleRoute>
            }
          />
          <Route
            path="/listar-personal"
            element={
              <RoleRoute allowedRoles={['ADMIN']}>
                <ListarPersonal />
              </RoleRoute>
            }
          />
          <Route
            path="/cargar-programacion"
            element={
              <RoleRoute allowedRoles={['ADMIN']}>
                <CargarProgramacion />
              </RoleRoute>
            }
          />

          {/* Operación diaria: ADMIN o COCINA */}
          <Route
            path="/almuerzos"
            element={
              <RoleRoute allowedRoles={['ADMIN', 'COCINA']}>
                <MachineRoute>
                  <AlmuerzoDelDia />
                </MachineRoute>
              </RoleRoute>
            }
          />

          {/* Reportes: ADMIN y AUDITOR */}
          <Route
            path="/pronostico"
            element={
              <RoleRoute allowedRoles={['ADMIN', 'AUDITOR']}>
                <PronosticoMes />
              </RoleRoute>
            }
          />
          <Route
            path="/menumes"
            element={
              <RoleRoute allowedRoles={['ADMIN']}>
                <MantenedorMenuMes />
              </RoleRoute>
            }
          />
          <Route
            path="/reporte"
            element={
              <RoleRoute allowedRoles={['ADMIN', 'AUDITOR']}>
                <ReportesGanancia />
              </RoleRoute>
            }
          />

          {/* ✅ Mantenedor usuarios: SOLO ADMIN */}
          <Route
            path="/usuarios"
            element={
              <RoleRoute allowedRoles={['ADMIN']}>
                <MantenedorUsuarios />
              </RoleRoute>
            }
          />

          {/* Dashboard Admin: solo ADMIN */}
          <Route
            path="/dashboardAdmin"
            element={
              <RoleRoute allowedRoles={['ADMIN']}>
                <DashboardAlmuerzosAdmin />
              </RoleRoute>
            }
          />

          {/* Dashboard Admin: solo ADMIN */}
          <Route
            path="/dashboard-sede"
            element={
              <RoleRoute allowedRoles={['ADMIN', 'COCINA', 'AUDITOR']}>
               <DashboardAlmuerzosSede />
              </RoleRoute>
            }
          />


        </Route>

        {/* fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  )
}

export default App
